import { Stack, Typography } from '@mui/material'

import {
  PriceProductDetails,
  PriceProductDetailsSkeletonFullPrice,
  PriceProductDetailsSkeletonMain,
  PriceProductDetailsSkeletonSave,
} from '@/components/ProductPrice/ProductPrice.styled'
import { ProductSaveLabels } from '@/components/ProductPrice/ProductSaveLabels'
import { SmallestUnitPrice } from '@/components/ProductPrice/SmallestUnitPrice'
import { PriceLabelEnum } from '@/enums/PriceLabelEnum'
import { getPriceMarkers } from '@/helpers/getPriceMarkers'
import { useTenant } from '@/hooks/useTenant'
import { useTranslations } from '@/hooks/useTranslations'
import { commerceTheme as theme } from '@/themes/commerce/theme'

import type React from 'react'
import type { getBffPrices } from '@/helpers/getBffPrices'
import type { ProductProps } from '@/interfaces/product.interface'

interface ProductDetailsPriceProps {
  product?: ProductProps
  price?: ReturnType<typeof getBffPrices>
  isLoading?: boolean
}
export const ProductDetailsPrice: React.FC<ProductDetailsPriceProps> = ({ product, price, isLoading = false }) => {
  const { translate } = useTranslations()
  const tenant = useTenant()

  const { fullPrice, saveLabel } = getPriceMarkers(price ?? null, tenant.currency, tenant.locale)

  return (
    <Stack paddingBottom="4px" width="100%" position="relative">
      {isLoading ? (
        <Stack>
          {fullPrice && <PriceProductDetailsSkeletonFullPrice />}
          {saveLabel && <PriceProductDetailsSkeletonSave />}
          <PriceProductDetailsSkeletonMain />
        </Stack>
      ) : (
        <PriceProductDetails
          sx={{
            color:
              fullPrice || price?.priceType === PriceLabelEnum.CAMPAIGN
                ? theme.palette.text.campaignPrice
                : theme.palette.text.primary,
          }}
        >
          {fullPrice && (
            <Typography component="p" sx={{ color: theme.palette.text.placeholder, marginLeft: 0.5 }} variant="body1">
              {translate(`productDetails.listPrice`)}
              <Typography
                sx={{
                  textDecoration: 'line-through',
                  marginLeft: 0.5,
                }}
                component="span"
              >
                {fullPrice}
              </Typography>
            </Typography>
          )}
          {saveLabel && (
            <ProductSaveLabels productDetails percentage={price?.discountPercentage} saveLabel={saveLabel} />
          )}
          {price?.formattedValue ?? product?.price?.formattedValue}
          <SmallestUnitPrice className="block font-normal text-sm" smallestUnitPrice={price?.smallestUnitPrice} />
        </PriceProductDetails>
      )}
    </Stack>
  )
}
